import { observer } from 'mobx-react-lite';
import React, { useContext, useEffect, useState } from 'react';
import { FaMinusCircle, FaPlusCircle } from 'react-icons/fa';
import { INPUT_AUTOFILL_PROPS } from '../InputField/autofillProps';
import { useTranslation } from '../../store/context/TranslationContext';
import { DialogContext } from '../../store/context/DialogContext';
import "./stripe.css"
import { FaDollarSign } from 'react-icons/fa6';
import { buildAutomationChildTestId, resolveAutomationTestId } from '../InputField/testId';

const STEP_CENTS = 10000; // $100 increments

const DepositAmountField = observer(({
    minInitialDepositAmount,
    maxInitialDepositAmount,
    value = null,                         // in cents
    handleDepositAmountChange,     // expects cents
    loading,
    onError = () => { },
    testId,
}) => {
    const { t } = useTranslation();
    const { showError } = useContext(DialogContext);

    const min = minInitialDepositAmount === 'N/A' ? 0 : (Number(minInitialDepositAmount) || 0) * 100;
    const max = maxInitialDepositAmount === 'N/A' || maxInitialDepositAmount === null || maxInitialDepositAmount === undefined || maxInitialDepositAmount === ''
        ? Number.POSITIVE_INFINITY
        : (Number(maxInitialDepositAmount) || 0) * 100;
    const hasMaxLimit = Number.isFinite(max);

    // string state for free typing
    const [inputValue, setInputValue] = useState(value != null ? (value / 100).toFixed(2) : (0).toFixed(2));
    const [error, setError] = useState(null);
    const resolvedTestId = resolveAutomationTestId(testId);

    const changeError = (v) => {
        setError(v);
        onError(v);
    }

    useEffect(() => {
        if (value === null || value == 0) {
            setInputValue((0).toFixed(2));
        } else {
            setInputValue((value / 100).toFixed(2));
        }
        changeError(null);
    }, [value]);

    const validateCents = (cents) => {
        if (cents == 0 || cents == null) return null
        if (cents < min) return t(`Minimum amount is $${(min / 100).toFixed(2)}`);
        if (hasMaxLimit && cents > max) return t(`Maximum amount is $${(max / 100).toFixed(2)}`);
        return null;
    };

    const parseInput = (raw) => {
        const cleaned = raw.replace(/[^0-9.]/g, '');
        const num = parseFloat(cleaned);
        if (isNaN(num)) return { cents: 0, error: null };
        const cents = Math.round(num * 100);
        return { cents, error: validateCents(cents) };
    };

    const handleBlur = () => {
        const { cents, error } = parseInput(inputValue);
        if (error) {
            showError(error);
            changeError(error);
        } else {
            changeError(null);
            const newValue = cents == null || !cents ? 0 : cents;
            if (value !== newValue) {
                handleDepositAmountChange(newValue);
            }
        }
        setInputValue((cents / 100).toFixed(2));
    };

    // Block invalid keystrokes & enforce max
    const handleKeyDown = (e) => {
        // allow all ctrl/meta combos (copy, paste, select-all…)
        if (e.ctrlKey || e.metaKey) return;

        const nav = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End'];
        if (nav.includes(e.key)) return;

        // only one dot
        if (e.key === '.') {
            if (inputValue.includes('.')) e.preventDefault();
            return;
        }

        // only digits
        if (!/^\d$/.test(e.key)) {
            e.preventDefault();
            return;
        }

        // Build the next string, then check decimals + max
        const { selectionStart, selectionEnd } = e.target;
        const nextRaw = inputValue.slice(0, selectionStart)
            + e.key
            + inputValue.slice(selectionEnd);

        // max two decimal places
        const idx = nextRaw.indexOf('.');
        if (idx >= 0 && nextRaw.slice(idx + 1).length > 2) {
            e.preventDefault();
            return;
        }

        // enforce max value
        const { cents } = parseInput(nextRaw);
        if (hasMaxLimit && cents > max) {
            e.preventDefault();
            const msg = t(`Maximum amount is $${(max / 100).toFixed(2)}`);
            changeError(msg);
        }
    };

    // Block oversized paste
    const handlePaste = (e) => {
        const paste = e.clipboardData.getData('text');
        const { selectionStart, selectionEnd } = e.target;
        const nextRaw = inputValue.slice(0, selectionStart)
            + paste
            + inputValue.slice(selectionEnd);

        const { cents } = parseInput(nextRaw);
        if (hasMaxLimit && cents > max) {
            e.preventDefault();
            const msg = t(`Maximum amount is $${(max / 100).toFixed(2)}`);
            changeError(msg);
        }
    };

    const handleChange = (e) => {
        setInputValue(e.target.value);
        if (error) changeError(null);
    };

    const handleStep = (dir) => {
        let next;

        if (dir === 1) {
            if (inputValue == null || inputValue == 0) {
                next = min;
            } else {
                next = (inputValue * 100) + STEP_CENTS;
            }
        } else {
            if ((inputValue * 100) <= min) {
                next = 0;
            } else {
                next = (inputValue * 100) - STEP_CENTS;
            }
        }

        if (next !== null && next > 0) {
            next = Math.max(min, next);
            if (hasMaxLimit) {
                next = Math.min(max, next);
            }
        }

        let err =( next === null || next < 0 || !next) ? 0 : validateCents(next);

        if (err) {
            showError(err);
            changeError(err);
        } else {
            changeError(null);
            handleDepositAmountChange(next);
            if (next === null || next == 0) {
                setInputValue((0).toFixed(2));
            }
        }
    };

    const widthCh = Math.max(inputValue.length * 1.6, 10);

    return (
        <div className="fv_payment_initial_deposit_container" data-testid={resolvedTestId}>
            <div className="d-flex align-items-center justify-content-end gap-2" data-testid={buildAutomationChildTestId(resolvedTestId, "controls")}>
                <div
                    className="fv_payment_input_button"
                    data-testid={buildAutomationChildTestId(resolvedTestId, "decrease")}
                    onClick={() => {
                        if (!(loading || (inputValue === null || inputValue == 0))) {
                            handleStep(-1)
                        }
                    }}
                >
                    <FaMinusCircle size={16} color={(inputValue === null || inputValue == 0) ? '#AFD1FA' : '#1176B0'} />
                </div>

                <div className="fv_payment_input_wrapper position-relative" data-testid={buildAutomationChildTestId(resolvedTestId, "input-wrap")}>
                    <span className="fv_payment_dollar_icon" data-testid={buildAutomationChildTestId(resolvedTestId, "currency-icon")}>
                        <FaDollarSign size={14} />
                    </span>
                    <input
                        {...INPUT_AUTOFILL_PROPS}
                        type="text"
                        data-testid={buildAutomationChildTestId(resolvedTestId, "input")}
                        value={inputValue}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        inputMode="decimal"
                        pattern="^\d*(\.\d{0,2})?$"
                        className="fv_payment_input form-control text-center"
                        disabled={loading}
                        placeholder="0.00"
                        style={{
                            paddingLeft: '1.5rem',
                            maxWidth: `${widthCh}ch`,
                        }}
                    />
                </div>

                <div
                    className="fv_payment_input_button"
                    data-testid={buildAutomationChildTestId(resolvedTestId, "increase")}
                    onClick={() => {
                        if (!(loading || (hasMaxLimit && inputValue !== 0 && ((inputValue * 100) >= max)))) {
                            handleStep(1)
                        }
                    }}

                >
                    <FaPlusCircle size={16} color={hasMaxLimit && inputValue !== 0 && ((inputValue * 100) >= max) ? '#AFD1FA' : '#1176B0'} />
                </div>
            </div>

            {error && (
                <div className="fv_payment_input_error text-danger mt-1" data-testid={buildAutomationChildTestId(resolvedTestId, "error")}>
                    {error}
                </div>
            )}
        </div>
    );
});

export default DepositAmountField;
